<template>
  <v-form
    ref="form"
    v-model="isValidationOk"
    lazy-validation>
    <v-container
      v-if="Maglink.IsTechnicianUser()"
      mt-4
      py-0
      fill-height
      fluid
      grid-list-xl>
      <v-layout
        wrap>
        <v-flex
          xs6
          md10
          text-xs-right>
          <v-btn
            :disabled="!isValidationOk || progessBar.loading"
            small
            class="mx-0 font-weight-light"
            color="dover-blue"
            @click="getDataSource">
            {{ $t('Load') }}
          </v-btn>
        </v-flex>
        <v-flex
          xs6
          md2
          text-xs-right>
          <MaglinkComponentCsvExportButton
            :is-validation-ok="isValidationOk"
            :loading="progessBar.loading"
            @csvExport="csvExport" />
        </v-flex>
        <v-flex
          xs12
          md12
          lg12>
          <MaglinkComponentLoadingBar
            :loading="progessBar.loading"
            :indeterminate="false"
            :max-value="progessBar.maxValue"
            :value="progessBar.currentValue" />
          <span>{{ itemCountLabel }}</span>
          <hr>
        </v-flex>
        <v-flex
          xs12
          md12
          lg12>
          <v-data-table
            :headers="headers"
            :items="tableDataSource"
            :no-data-text="`${Maglink.NoDataMessage(false)}`"
            :no-results-text="`${Maglink.NoDataMessage(true)}`"
            hide-actions>
            <template
              slot="headerCell"
              slot-scope="{ header }">
              <v-layout
                align-center
                column
                ma-0>
                <v-flex
                  align-self-center
                  shrink>
                  <span
                    class="dover-light-blue-color"
                    v-text="header.text" />
                </v-flex>
                <v-flex
                  align-self-center
                  shrink>
                  <span
                    class="dover-light-blue-color h6"
                    v-text="header.subtext" />
                </v-flex>
              </v-layout>
            </template>
            <template
              slot="items"
              slot-scope="{ index, item }">
              <tr
                :style="{ backgroundColor: backColor(index, item) }"
                class="text-xs-right">
                <td class="text-xs-left">{{ convertTime(item.time) }}</td>
                <td>{{ convertLength(item.length) }}</td>
                <td>{{ convertLeakage(item.mn) }}</td>
                <td>{{ convertLeakage(item.smn) }}</td>
                <td>{{ convertTemperature(item.sta) }}</td>
                <td>{{ convertHeight(item.sht) }}</td>
                <td>{{ convertTemperatureSlope(item.mt) }}</td>
                <td>{{ convertTemperatureSlope(item.mr1) }}</td>
                <td>{{ convertHeight(item.mh) }}</td>
                <td>{{ convertTemperature(item.tave) }}</td>
                <td>{{ convertHeight(item.ht) }}</td>
              </tr>
            </template>
          </v-data-table>
        </v-flex>
        <v-flex
          xs12
          md12
          lg12>
          <hr>
        </v-flex>
        <v-flex
          xs12
          md12
          lg12
          text-xs-center>
          <apexchart
            :options="lineChart.options"
            :series="lineChart.series"
            height= "450"
            width= "100%"
            type= "line" />
        </v-flex>
      </v-layout>
    </v-container>
    <v-container
      v-else
      mt-4
      py-0
      fill-height
      fluid
      grid-list-xl>
      <v-layout
        wrap>
        <v-flex
          xs12
          md12>
          <span>{{ $t('Information available only for technical service') }}</span>
        </v-flex>
      </v-layout>
    </v-container>
  </v-form>
</template>

<script>

import { i18n } from '@/i18n'

export default {
  props: {
    tanks: {
      type: Array,
      required: true,
      default: () => []
    },
    systemSettings: {
      type: Object,
      required: true,
      default: () => { }
    },
    options: {
      type: Object,
      required: true,
      default: () => { }
    },
    siteInfo: {
      type: Object,
      required: true,
      default: () => { }
    },
    filters: {
      type: Object,
      required: true,
      default: () => { }
    },
    csldReasonDesc: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  data: () => ({
    isValidationOk: false,
    progessBar: {
      loading: false,
      maxValue: 100,
      currentValue: 0
    },
    rules: {
      isDate: [v => true]
    },
    headers: [],
    dataSource: [],
    statistics: [],
    tableDataSource: [],
    levUdm: '',
    tempUdm: '',
    volUdm: '',
    itemCountLabel: '',
    graphDataSource: {
      segments: [],
      volume: []
    },
    lineChart: {
      series: [{
        name: '',
        data: []
      }],
      options: {
        chart: {
          type: 'line',
          zoom: {
            enabled: false
          }
        },
        grid: {
          xaxis: {
            lines: {
              show: true
            }
          },
          column: {
            colors: ['#e5e5e5', 'transparent']
          }
        },
        dataLabels: {
          enabled: false
        },
        stroke: {
          curve: 'straight'
        },
        noData: {
          text: i18n.t('Loading...'),
          style: {
            fontSize: '26px'
          }
        },
        xaxis: {
          type: 'datetime',
          title: { text: '' }
        },
        tooltip: {
          x: {
            formatter: function (val) {
              let dt = new Date(val)
              return dt.toISOString().substr(11, 5)
            }
          }
        },
        title: {
          text: '',
          align: 'center'
        },
        yaxis: {
          title: {
            text: ''
          }
        }
      }
    }
  }),
  watch: {
    filters: {
      immediate: true,
      deep: true,
      handler (newVal, oldVal) {
        this.dataSource = []
        this.statistics = []
        this.tableDataSource = []
        this.graphDataSource = { segments: [], volume: [] }
      }
    }
  },
  mounted () {
    this.levUdm = this.options.levelUnits[this.systemSettings.levelUnit || 0].desc
    this.tempUdm = this.options.temperatureUnits.find(c => c.code === this.systemSettings.temperatureUnit).desc
    this.volUdm = this.options.volumeUnits[this.systemSettings.volumeUnit || 0].desc

    this.headers = [
      { sortable: false, text: this.$t('Time'), subtext: '', align: 'right' },
      { sortable: false, text: this.$t('Length'), subtext: '', align: 'right' },
      { sortable: false, text: this.$t('mn'), subtext: this.Maglink.LeakShortUdm(this.systemSettings.volumeUnit), align: 'center' },
      { sortable: false, text: this.$t('smn'), subtext: this.Maglink.LeakShortUdm(this.systemSettings.volumeUnit), align: 'center' },
      { sortable: false, text: this.$t('sta'), subtext: this.tempUdm, align: 'center' },
      { sortable: false, text: this.$t('sht'), subtext: this.levUdm, align: 'center' },
      { sortable: false, text: this.$t('mt'), subtext: this.tempUdm + '/h', align: 'center' },
      { sortable: false, text: this.$t('mr1'), subtext: this.tempUdm + '/h', align: 'center' },
      { sortable: false, text: this.$t('mh'), subtext: this.levUdm + '/h', align: 'center' },
      { sortable: false, text: this.$t('tave'), subtext: this.tempUdm, align: 'center' },
      { sortable: false, text: this.$t('ht'), subtext: this.levUdm, align: 'center' }
    ]
  },
  methods: {
    backColor (index, item) {
      if (item.segmentId === '') return 'transparent'

      return ((index % 2 === 0) ? '#cfdbfb' : 'transparent')
    },
    convertTime (value) {
      let result = this.Maglink.GetCustomDateTime(value, this.systemSettings.dateFormat)

      return result === '--' ? value : result
    },
    convertHeight (value) {
      if (value === '') return ''

      let data = Number(parseFloat(value)).toFixed(10) * 1.0

      return this.Maglink.LevelToDisplay(data, this.systemSettings.levelUnit)
    },
    convertLeakage (value) {
      if (value === '') return ''

      let data = Number(parseFloat(value)).toFixed(10) * 1.0

      let result = this.Maglink.LeakageFormat(this.Maglink.LeakageToDisplay(data, this.systemSettings.volumeUnit))
      return Number.isNaN(result) ? this.Maglink.RoundFormat(0.0, 3) : result
    },
    convertLength (value) {
      if (value === '') return ''

      return this.Maglink.GetHourMinuteSecondFormatOfSeconds(value)
    },
    convertNumber (value, decimal) {
      if (value === '') return ''

      let data = Number(parseFloat(value)).toFixed(10).toString()
      return this.Maglink.RoundFormat(data, decimal)
    },
    convertTemperature (value) {
      if (value === '') return ''

      let data = Number(parseFloat(value)).toFixed(10) * 1.0

      if (data * 1.0 >= 999.0) {
        return '-'
      }

      return this.Maglink.TemperatureToDisplay(data, this.systemSettings.temperatureUnit)
    },
    convertTemperatureSlope (value) {
      if (value === '') return ''

      let data = Number(parseFloat(value)).toFixed(10) * 1.0

      if (data * 1.0 >= 999.0) {
        return '-'
      }

      return this.Maglink.TemperatureSlopeToDisplay(data, this.systemSettings.temperatureUnit)
    },
    csvExport (sep) {
      let fileContent = ''
      let title = ''

      if (this.siteInfo.name) {
        fileContent += this.siteInfo.name + '\n'
      }
      if (this.siteInfo.header1) {
        fileContent += this.siteInfo.header1 + '\n'
      }
      if (this.siteInfo.header2) {
        fileContent += this.siteInfo.header2 + '\n'
      }
      if (this.siteInfo.header3) {
        fileContent += this.siteInfo.header3 + '\n'
      }

      // filter
      if (this.filters.all) {
        title = this.$t('All tanks')
        fileContent += title + '\n'
      } else {
        title = this.$t('Tank {0}', [this.filters.number])
        fileContent += title + sep
        fileContent += this.tanks[this.filters.number - 1].tankDescription + '\n'
      }
      fileContent += '\n'

      fileContent += this.$t('Time') + sep
      fileContent += this.$t('Length') + sep
      fileContent += this.$t('mn') + ' (' + this.Maglink.LeakShortUdm(this.systemSettings.volumeUnit) + ')' + sep
      fileContent += this.$t('smn') + ' (' + this.Maglink.LeakShortUdm(this.systemSettings.volumeUnit) + ')' + sep
      fileContent += this.$t('sta') + ' (' + this.tempUdm + ')' + sep
      fileContent += this.$t('sht') + ' (' + this.levUdm + ')' + sep
      fileContent += this.$t('mt') + ' (' + this.tempUdm + '/h)' + sep
      fileContent += this.$t('mr1') + ' (' + this.tempUdm + '/h)' + sep
      fileContent += this.$t('mh') + ' (' + this.levUdm + '/h)' + sep
      fileContent += this.$t('tave') + ' (' + this.tempUdm + ')' + sep
      fileContent += this.$t('ht') + ' (' + this.levUdm + ')' + sep
      fileContent += '\n'

      for (let index = 0; index < this.dataSource.length; index++) {
        let t = this.dataSource[index]
        fileContent += this.Maglink.GetCustomDateTime(t.time, this.systemSettings.dateFormat).substr(0, 16) + sep
        fileContent += this.Maglink.GetHourMinuteSecondFormatOfSeconds(t.length) + sep
        fileContent += this.convertLeakage(t.mn) + sep
        fileContent += this.convertLeakage(t.smn) + sep
        fileContent += this.convertTemperature(t.sta, 5) + sep
        fileContent += this.convertHeight(t.sht) + sep
        fileContent += this.convertTemperatureSlope(t.mt) + sep
        fileContent += this.convertTemperatureSlope(t.mr1) + sep
        fileContent += this.convertHeight(t.mh) + sep
        fileContent += this.convertTemperature(t.tave) + sep
        fileContent += this.convertHeight(t.ht) + '\n'
      }

      fileContent += '\n' + sep
      fileContent += this.$t('Length') + sep
      fileContent += this.$t('mn') + ' (' + this.Maglink.LeakShortUdm(this.systemSettings.volumeUnit) + ')' + sep
      fileContent += this.$t('smn') + ' (' + this.Maglink.LeakShortUdm(this.systemSettings.volumeUnit) + ')' + sep
      fileContent += this.$t('sta') + ' (' + this.tempUdm + ')' + sep
      fileContent += this.$t('sht') + ' (' + this.levUdm + ')' + sep
      fileContent += this.$t('mt') + ' (' + this.tempUdm + '/h)' + sep
      fileContent += this.$t('mr1') + ' (' + this.tempUdm + '/h)' + sep
      fileContent += this.$t('mh') + ' (' + this.levUdm + '/h)' + sep
      fileContent += this.$t('tave') + ' (' + this.tempUdm + ')' + sep
      fileContent += this.$t('ht') + ' (' + this.levUdm + ')' + sep
      fileContent += '\n'

      for (let index = 0; index < this.statistics.length; index++) {
        let t = this.statistics[index]
        fileContent += t.mode + sep
        fileContent += this.Maglink.GetHourMinuteSecondFormatOfSeconds(t.length) + sep
        fileContent += this.convertLeakage(t.mn) + sep
        fileContent += this.convertLeakage(t.smn) + sep
        fileContent += this.convertTemperature(t.sta) + sep
        fileContent += this.convertHeight(t.sht) + sep
        fileContent += this.convertTemperatureSlope(t.mt) + sep
        fileContent += this.convertTemperatureSlope(t.mr1) + sep
        fileContent += this.convertHeight(t.mh) + sep
        fileContent += this.convertTemperature(t.tave) + sep
        fileContent += this.convertHeight(t.ht) + '\n'
      }

      let filename = this.$t('CSLD Segments report') + '__' + title
      filename += '.csv'

      const link = this.Maglink.GetDownloadLinkCSV(fileContent, filename)
      document.body.appendChild(link)
      link.click()
    },
    getDataSource () {
      this.progessBar.loading = true
      this.dataSource = []

      this.Maglink.GetReportCsldSegments(this.filters.number)
        .then(response => {
          let length = response.data.segments.length
          this.progessBar.maxValue = length

          for (var i = 0; i < length; i++) {
            this.progessBar.currentValue = i
            this.dataSource.push(response.data.segments[i])
          }

          this.dataSource.sort(function (a, b) {
            return b.time - a.time
          })

          this.progessBar.loading = false
          this.itemCountLabel = this.$t('Items: {0}', [length])

          this.tableDataSource = this.dataSource.map(a => { return { ...a } })
          this.tableDataSource.push(
            {
              time: '',
              length: '',
              mn: '',
              smn: '',
              sta: '',
              sht: '',
              mt: '',
              mr1: '',
              mh: '',
              tave: '',
              ht: '',
              inconclusiveCode: '',
              segmentId: '',
              tankId: this.filters.number,
              valid: false
            },
            {
              time: this.$t('Max'),
              length: response.data.max.length,
              mn: response.data.max.mn,
              smn: response.data.max.smn,
              sta: response.data.max.sta,
              sht: response.data.max.sht,
              mt: response.data.max.mt,
              mr1: response.data.max.mr1,
              mh: response.data.max.mh,
              tave: response.data.max.tave,
              ht: response.data.max.ht,
              inconclusiveCode: 0,
              segmentId: '',
              tankId: this.filters.number,
              valid: true
            },
            {
              time: this.$t('Avg'),
              length: response.data.avg.length,
              mn: response.data.avg.mn,
              smn: response.data.avg.smn,
              sta: response.data.avg.sta,
              sht: response.data.avg.sht,
              mt: response.data.avg.mt,
              mr1: response.data.avg.mr1,
              mh: response.data.avg.mh,
              tave: response.data.avg.tave,
              ht: response.data.avg.ht,
              inconclusiveCode: 0,
              segmentId: '',
              tankId: this.filters.number,
              valid: true
            },
            {
              time: this.$t('Min'),
              length: response.data.min.length,
              mn: response.data.min.mn,
              smn: response.data.min.smn,
              sta: response.data.min.sta,
              sht: response.data.min.sht,
              mt: response.data.min.mt,
              mr1: response.data.min.mr1,
              mh: response.data.min.mh,
              tave: response.data.min.tave,
              ht: response.data.min.ht,
              inconclusiveCode: 0,
              segmentId: '',
              tankId: this.filters.number,
              valid: true
            }
          )

          this.statistics = [
            {
              mode: this.$t('Max'),
              length: response.data.max.length,
              mn: response.data.max.mn,
              smn: response.data.max.smn,
              sta: response.data.max.sta,
              sht: response.data.max.sht,
              mt: response.data.max.mt,
              mr1: response.data.max.mr1,
              mh: response.data.max.mh,
              tave: response.data.max.tave,
              ht: response.data.max.ht
            },
            {
              mode: this.$t('Avg'),
              length: response.data.avg.length,
              mn: response.data.avg.mn,
              smn: response.data.avg.smn,
              sta: response.data.avg.sta,
              sht: response.data.avg.sht,
              mt: response.data.avg.mt,
              mr1: response.data.avg.mr1,
              mh: response.data.avg.mh,
              tave: response.data.avg.tave,
              ht: response.data.avg.ht
            },
            {
              mode: this.$t('Min'),
              length: response.data.min.length,
              mn: response.data.min.mn,
              smn: response.data.min.smn,
              sta: response.data.min.sta,
              sht: response.data.min.sht,
              mt: response.data.min.mt,
              mr1: response.data.min.mr1,
              mh: response.data.min.mh,
              tave: response.data.min.tave,
              ht: response.data.min.ht
            }
          ]

          if (this.dataSource.length > 0) {
            let length = this.dataSource.length
            if (this.dataSource[0].time !== 0 && this.dataSource[length - 1].time !== 0) {
              let maxDate = this.Maglink.GetCustomDateTime(this.dataSource[0].time, 'yyyy-mm-dd')
              let minDate = this.Maglink.GetCustomDateTime(this.dataSource[length - 1].time, 'yyyy-mm-dd')

              let localFilters = {
                all: false,
                number: this.filters.number,
                type: 0,
                fromDay: minDate,
                toDay: maxDate
              }

              this.graphDataSource.segments = []
              this.groupSegments(this.dataSource)

              this.Maglink.GetReportProductVolume(localFilters)
                .then(response => {
                  this.graphDataSource.volume = response.data
                  this.graphDataSource.volume.sort(function (a, b) {
                    return b.measuredOn - a.measuredOn
                  })

                  this.loadGraph()
                })
                .catch(error => {
                  this.progessBar.loading = false
                  this.snack('error', error.message)
                })
            }
          } else {
            this.loadGraphEmpty()
          }
        })
        .catch(error => {
          this.progessBar.loading = false
          this.snack('error', error.message)
        })
    },
    groupSegments (segments) {
      let dates = []

      segments.forEach(s => {
        let dt = new Date(s.time * 1000)
        let dc = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate(), 0, 0, 0, 0)
        if (dates.filter(d => d.getTime() === dc.getTime()).length === 0) {
          dates.push(dc)
        }
      })

      if (dates.length === 0) return

      dates.forEach(d => {
        let seconds = (new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0, 0)).getTime() / 1000
        let min = d.getTime() / 1000
        const d1 = d
        d1.setDate(d1.getDate() + 1)

        let max = d1.getTime() / 1000

        let valids = segments.filter(s => s.time >= min && s.time < max && s.valid).length
        let invalids = segments.filter(s => s.time >= min && s.time < max && !s.valid).length

        this.graphDataSource.segments.push({ date: seconds, valid: valids, invalid: invalids })
      })
    },
    loadGraph () {
      if (this.graphDataSource.volume.length === 0 && this.graphDataSource.segments.length === 0) {
        this.loadGraphEmpty()
        return
      }

      let validSegments = []
      let invalidSegments = []
      let volume = []

      this.graphDataSource.segments.forEach(item => {
        let xValue = this.Maglink.GetUtcToLocalTime(item.date * 1000).date
        validSegments.push([xValue, item.valid])
        invalidSegments.push([xValue, item.invalid])
      })

      this.graphDataSource.volume.forEach(item => {
        let xValue = this.Maglink.GetUtcToLocalTime(item.measuredOn * 1000).date
        volume.push([xValue, this.Maglink.VolumeToDisplay(item.productVolume, this.systemSettings.volumeUnit)])
      })

      let xLabel = this.$t('Date') + ': '

      this.lineChart = {
        series: [
          { type: 'area', name: this.$t('Volume'), data: volume },
          { type: 'line', name: this.$t('Valid segments'), data: validSegments },
          { type: 'line', name: this.$t('Invalid segments'), data: invalidSegments }
        ],
        options: {
          chart: {
            type: 'line',
            zoom: {
              enabled: false
            }
          },
          grid: {
            xaxis: {
              lines: {
                show: true
              }
            },
            column: {
              colors: ['#e5e5e5', 'transparent']
            }
          },
          dataLabels: {
            enabled: true,
            enabledOnSeries: [1, 2]
          },
          noData: {
            text: i18n.t('Loading...'),
            style: {
              fontSize: '26px'
            }
          },
          stroke: {
            curve: 'straight'
          },
          fill: {
            type: 'solid',
            opacity: [0.35, 1]
          },
          xaxis: {
            type: 'datetime',
            title: { text: '' }
          },
          tooltip: {
            enabledOnSeries: [0],
            intersect: false,
            x: {
              formatter: function (val) {
                let dt = new Date(val)
                return xLabel + dt.toISOString()
              }
            }
          },
          title: {
            text: '',
            align: 'left'
          },
          yaxis: [
            {
              opposite: true,
              decimalsInFloat: 0,
              axisBorder: {
                show: true
              },
              axisTicks: {
                show: true
              },
              title: {
                text: this.volUdm
              }
            },
            {
              decimalsInFloat: 0,
              axisBorder: {
                show: true
              },
              axisTicks: {
                show: true
              },
              title: {
                text: this.$t('Valid segments')
              }
            },
            {
              decimalsInFloat: 0,
              axisTicks: {
                show: true
              },
              title: {
                text: this.$t('Invalid segments')
              }
            }
          ]
        }
      }
    },
    loadGraphEmpty () {
      this.lineChart = {
        series: [{
          name: '',
          data: []
        }],
        options: {
          chart: {
            type: 'line',
            zoom: {
              enabled: false
            }
          },
          grid: {
            xaxis: {
              lines: {
                show: true
              }
            },
            column: {
              colors: ['#e5e5e5', 'transparent']
            }
          },
          dataLabels: {
            enabled: false
          },
          stroke: {
            curve: 'straight'
          },
          noData: {
            text: this.Maglink.NoDataMessage(false),
            style: {
              fontSize: '26px'
            }
          },
          xaxis: {
            type: 'datetime',
            title: { text: '' }
          },
          tooltip: {
            x: {
              formatter: function (val) {
                let dt = new Date(val)
                return dt.toISOString().substr(11, 5)
              }
            }
          },
          title: {
            text: '',
            align: 'center'
          },
          yaxis: {
            title: {
              text: ''
            }
          }
        }
      }
    },

    snack (color, message) {
      message = 'CSLD ' + this.$t('Segments') + ': ' + message
      this.$store.dispatch('showSnackbar', { color, message, show: true })
    }
  }
}
</script>
